import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/buchungsheld-db/server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { LogoConfig, MailTemplate } from '@adornis/mails/db/mail-template.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { defer, type Observable, switchMap, map, of } from 'rxjs';
import { Company } from '../db/company.js';
import { CompanyRegistration } from '../db/company-registration.js';
import { CompanyRegistrationStatus } from '../db/enums/registration-status.js';
import { MatchingWeekSettings } from '../db/settings.js';
import {
  CompanyRegistrationRejectedMail,
  CompanyRegistrationSubmittedMail,
  CompanyUserAddedMail,
} from '../mails/company-mails.js';
import { MailTemplateType } from '../mails/mail-templates.js';
import { normalizeSingleEmail } from '../shared/email.js';
import { assertCallerIsAdmin } from './admin-guard.js';

// ─── Public: Submit a company registration request ───

export const COMPANY_REGISTRATION_CLOSED_ERROR = 'Company registration is closed';

export const submitCompanyRegistration = registerMutation({
  operationName: 'submitCompanyRegistration',
  type: () => Boolean,
  params: [
    { name: 'companyName', type: () => String },
    { name: 'contactFirstName', type: () => String },
    { name: 'contactLastName', type: () => String },
    { name: 'contactEmail', type: () => String },
    { name: 'address', type: () => String },
    { name: 'sector', type: () => String },
    { name: 'website', type: () => String },
    { name: 'services', type: () => String },
    { name: 'openPositions', type: () => String },
    { name: 'participationReason', type: () => String },
    { name: 'contactPosition', type: () => String },
    { name: 'termsAccepted', type: () => Boolean },
    { name: 'participationAgreementAccepted', type: () => Boolean },
  ],
  resolve:
    (
      companyName: string,
      contactFirstName: string,
      contactLastName: string,
      contactEmail: string,
      address: string,
      sector: string,
      website: string,
      services: string,
      openPositions: string,
      participationReason: string,
      contactPosition: string,
      termsAccepted: boolean,
      participationAgreementAccepted: boolean,
    ) =>
    async () => {
      const settingsCollection = mongo().collection(MatchingWeekSettings._collectionName);
      const settings = await settingsCollection.findOne({}, { projection: { companyRegistrationClosed: 1 } });
      if (settings?.companyRegistrationClosed === true) throw new Error(COMPANY_REGISTRATION_CLOSED_ERROR);

      if (!termsAccepted) throw new Error('Nutzungsbedingungen für Unternehmen müssen akzeptiert werden');
      if (!participationAgreementAccepted) throw new Error('Teilnahmevereinbarung muss akzeptiert werden');
      if (!companyName?.trim()) throw new Error('Company name is required');
      if (!contactEmail?.trim()) throw new Error('Contact email is required');
      if (!contactFirstName?.trim()) throw new Error('Contact first name is required');
      if (!contactLastName?.trim()) throw new Error('Contact last name is required');

      // Reject malformed / multi-address input before it can become a User.email
      const normalizedEmail = normalizeSingleEmail(contactEmail);

      // Check for duplicate pending registration
      const col = mongo().collection(CompanyRegistration._collectionName);
      const existing = await col.findOne({ contactEmail: normalizedEmail, status: CompanyRegistrationStatus.PENDING });
      if (existing) throw new Error('A registration with this email is already pending review');

      const registration = new CompanyRegistration({
        companyName: companyName.trim(),
        contactFirstName: contactFirstName.trim(),
        contactLastName: contactLastName.trim(),
        contactEmail: normalizedEmail,
        address: address?.trim() || undefined,
        sector: sector?.trim() || undefined,
        website: website?.trim() || undefined,
        services: services?.trim() || undefined,
        openPositions: openPositions?.trim() || undefined,
        participationReason: participationReason?.trim() || undefined,
        contactPosition: contactPosition?.trim() || undefined,
      });
      await registration.create();

      // Notify admin via email
      try {
        const _mailer = await mailer;
        if (_mailer) {
          const settingsCol = mongo().collection(MatchingWeekSettings._collectionName);
          const settings = await settingsCol.findOne({});
          const adminEmail = settings?.adminNotificationEmail;
          if (adminEmail) {
            await CompanyRegistrationSubmittedMail(registration).send({
              mailer: _mailer,
              recipients: [new Recipient(adminEmail)],
            });
          }
        }
      } catch (e) {
        // Don't fail the registration if the notification email fails
        console.error('Failed to send admin notification email', e);
      }

      return true;
    },
});

// ─── Admin: List all registrations ───

export const subscribeCompanyRegistrations = registerSubscription({
  operationName: 'subscribeCompanyRegistrations',
  type: () => [CompanyRegistration],
  resolve:
    () =>
    <SelSet extends BaseQLSelectionSet<CompanyRegistration>>(gqlFields: SelSet) => {
      return defer(() => assertCallerIsAdmin()).pipe(
        switchMap(
          () =>
            watchQuery(CompanyRegistration._collectionName, {}) as Observable<
              NarrowedEntity<CompanyRegistration, SelSet>[]
            >,
        ),
      );
    },
});

// ─── Admin: Count pending registrations ───

export const countPendingRegistrations = registerQuery({
  operationName: 'countPendingRegistrations',
  type: () => Int,
  resolve: () => async () => {
    await assertCallerIsAdmin();
    const collection = await scopedCollection(CompanyRegistration);
    return collection.countDocuments({ status: CompanyRegistrationStatus.PENDING });
  },
});

// ─── Admin: Approve a registration ───

export const approveCompanyRegistration = registerMutation({
  operationName: 'approveCompanyRegistration',
  type: () => Boolean,
  params: [{ name: 'registrationID', type: () => ID }],
  resolve: (registrationID: string) => async () => {
    await assertCallerIsAdmin();
    const registration = await getCompanyRegistrationByID(registrationID)(selectionSet(() => CompanyRegistration, 1));
    if (!registration) throw new Error('Registration not found');
    if (registration.status !== CompanyRegistrationStatus.PENDING) throw new Error('Registration is not pending');

    // Defense-in-depth: never mint a User from a malformed / multi-address contact email
    // (older registrations predate submit-time validation).
    const contactEmail = normalizeSingleEmail(registration.contactEmail);

    // 1. Create the Company entity
    const company = new Company({
      name: registration.companyName,
      address: registration.address,
      sector: registration.sector,
      website: registration.website,
      services: registration.services,
      userIDs: [],
    });
    await company.create();

    // 2. Create or find user for the contact person
    const userCol = mongo().collection(User._collectionName);
    const emailSelector = { $regex: `^${contactEmail.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' };
    const existingUser = await userCol.findOne({ email: emailSelector });

    let userID: string;
    if (existingUser) {
      userID = existingUser._id as unknown as string;
    } else {
      const newUser = new User({
        email: contactEmail,
        username: contactEmail,
        firstName: registration.contactFirstName,
        lastName: registration.contactLastName,
      } as any);
      userID = await createNewUser(newUser as any, undefined as any, false);
    }

    // 3. Add user to company (role auto-assigned by update-roles-on-users.ts watcher)
    const companyCol = mongo().collection<Company>(Company._collectionName);
    await companyCol.updateOne({ _id: company._id }, { $addToSet: { userIDs: userID } });

    // 4. Send welcome email to the company rep, including a magic-login link.
    // The token signs the recipient in instantly when they click — no need to
    // run the "Login mit E-Mail" OTP dance first. ttu: 99 so reopening the
    // mail still works; ttl: 14 days matches the copy in the template.
    try {
      const _mailer = await mailer;
      if (_mailer) {
        const user = await getUserByID(userID)({ email: 1, username: 1, firstName: 1, lastName: 1 });
        if (user) {
          const loginToken = await generateLoginToken({
            userID,
            userClass: User._class,
            ttu: 99,
            ttl: 1000 * 60 * 60 * 24 * 14,
          });
          const loginLink = A.absoluteUrl('/action/login', new URLSearchParams({ token: loginToken }));
          await CompanyUserAddedMail(user, loginLink).send({
            mailer: _mailer,
            recipients: [new Recipient(user.email)],
          });
        }
      }
    } catch (e) {
      console.error('Failed to send company user welcome email', e);
    }

    // 5. Update registration status
    registration.status = CompanyRegistrationStatus.APPROVED;
    registration.reviewedAt = new Date();
    registration.reviewedByUserID = context.serverContext ? undefined : context.userID;
    registration.resultingCompanyID = company._id;
    await registration.save();

    return true;
  },
});

// ─── Admin: Reject a registration ───

export const rejectCompanyRegistration = registerMutation({
  operationName: 'rejectCompanyRegistration',
  type: () => Boolean,
  params: [
    { name: 'registrationID', type: () => ID },
    { name: 'rejectionReason', type: () => String },
  ],
  resolve: (registrationID: string, rejectionReason: string) => async () => {
    await assertCallerIsAdmin();
    const registration = await getCompanyRegistrationByID(registrationID)(selectionSet(() => CompanyRegistration, 1));
    if (!registration) throw new Error('Registration not found');
    if (registration.status !== CompanyRegistrationStatus.PENDING) throw new Error('Registration is not pending');

    registration.status = CompanyRegistrationStatus.REJECTED;
    registration.rejectionReason = rejectionReason?.trim() || undefined;
    registration.reviewedAt = new Date();
    registration.reviewedByUserID = context.serverContext ? undefined : context.userID;
    await registration.save();

    // Send rejection email
    try {
      const _mailer = await mailer;
      if (_mailer) {
        await CompanyRegistrationRejectedMail(registration).send({
          mailer: _mailer,
          recipients: [new Recipient(registration.contactEmail)],
        });
      }
    } catch (e) {
      console.error('Failed to send rejection email', e);
    }

    return true;
  },
});

// ─── Admin: Sync email banner to all mail templates ───

export const syncEmailBannerToTemplates = registerMutation({
  operationName: 'syncEmailBannerToTemplates',
  type: () => Boolean,
  resolve: () => async () => {
    await assertCallerIsAdmin();
    const settingsCol = await scopedCollection(MatchingWeekSettings);
    const settings = await settingsCol.findOne({});
    const templateCol = mongo().collection(MailTemplate._collectionName);

    const mwFilter = { _class: MailTemplate._class, emailType: { $in: Object.values(MailTemplateType) } };

    if (settings?.emailBannerFileId) {
      const bannerUrl = A.absoluteUrl(ImageFile.getServeLink(settings.emailBannerFileId));
      const logoConfig = new LogoConfig({ src: bannerUrl, alt: 'Matching Week', width: 600, padding: 0 }).toObject();
      await templateCol.updateMany(mwFilter, { $set: { logoConfig } });
    } else {
      await templateCol.updateMany(mwFilter, { $unset: { logoConfig: '' } });
    }

    return true;
  },
});

export const getCompanyRegistrationsByIDs = registerQuery({
  operationName: 'getCompanyRegistrationsByIDs',
  type: () => [CompanyRegistration],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<CompanyRegistration>>(gqlFields: SelSet) =>
      readEntitiesByIDs(CompanyRegistration, ids, gqlFields),
});

export const subscribeCompanyRegistrationsByIDs = registerSubscription({
  operationName: 'subscribeCompanyRegistrationsByIDs',
  type: () => [CompanyRegistration],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<CompanyRegistration>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(CompanyRegistration, ids, gqlFields),
});

export const getAllCompanyRegistrations = registerQuery({
  operationName: 'getAllCompanyRegistrations',
  type: () => [CompanyRegistration],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<CompanyRegistration>>(gqlFields: SelSet) =>
      readAllEntities(CompanyRegistration, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllCompanyRegistrations = registerSubscription({
  operationName: 'subscribeAllCompanyRegistrations',
  type: () => [CompanyRegistration],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<CompanyRegistration>>(gqlFields: SelSet) =>
      watchAllEntities(CompanyRegistration, gqlFields, { skip, limit, excludeIDs }),
});

export const getCompanyRegistrationByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<CompanyRegistration>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getCompanyRegistrationsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeCompanyRegistrationByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<CompanyRegistration>>(gqlFields: SelSet) =>
    id ? subscribeCompanyRegistrationsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
